import { ReactNode, useEffect, useState } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache, { EmotionCache } from '@emotion/cache';
import { useTranslation } from 'react-i18next';

interface RTLProviderProps {
    children: ReactNode;
}

// Create a single stable cache without the problematic RTL plugin
// RTL is handled via CSS logical properties and MUI's theme direction
// Emotion cache includes prefixing by default, no need to add stylisPlugins
const emotionCache: EmotionCache = createCache({
    key: 'mui',
    prepend: true,
});

/**
 * RTL Provider Component
 * Handles document direction and provides Emotion cache.
 * RTL styling is handled by:
 * 1. MUI theme direction (set in ThemeProviderWrapper)
 * 2. CSS logical properties (start/end instead of left/right)
 * 3. Document dir attribute for native browser RTL support
 */
export const RTLProvider = ({ children }: RTLProviderProps) => {
    const { i18n } = useTranslation();
    const [isRtl, setIsRtl] = useState(i18n.language === 'ar');

    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            setIsRtl(lng === 'ar');
        };
        
        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n]);

    useEffect(() => {
        document.dir = isRtl ? 'rtl' : 'ltr';
        document.documentElement.lang = i18n.language;
        document.body.classList.toggle('rtl', isRtl);
        document.body.classList.toggle('ltr', !isRtl);
    }, [isRtl, i18n.language]);

    return (
        <CacheProvider value={emotionCache}>
            {children}
        </CacheProvider>
    );
};
